import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Search, Image as ImageIcon, FileText, Trash2, Copy, ExternalLink,
  Loader2, Filter, Upload, CheckCircle2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import MediaUploadField from "./MediaUploadField";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatBytes = (bytes) => {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function MediaLibrary() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState(""); // "", image, pdf
  const [search, setSearch] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [showUploader, setShowUploader] = useState(false);
  const [uploaderKind, setUploaderKind] = useState("image");

  const fetchLibrary = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const params = new URLSearchParams();
      if (kindFilter) params.set("kind", kindFilter);
      if (search.trim()) params.set("search", search.trim());
      const res = await fetch(`${API_URL}/api/media/library?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setItems(data.items || []);
    } catch (err) {
      console.error("Error fetching library:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLibrary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kindFilter]);

  const handleDelete = async (id) => {
    if (!window.confirm("Archive this asset? It will be hidden from the library.")) return;
    try {
      const token = localStorage.getItem("auth_token");
      await fetch(`${API_URL}/api/media/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchLibrary();
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const handleCopy = (item) => {
    const url = `${API_URL}${item.public_url}`;
    navigator.clipboard.writeText(url);
    setCopiedId(item.id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  return (
    <div className="space-y-6" data-testid="media-library">
      {/* Toolbar */}
      <div className="flex flex-col lg:flex-row gap-3 items-start lg:items-center justify-between">
        <div className="flex items-center gap-3 w-full lg:w-auto">
          <div className="relative flex-1 lg:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") fetchLibrary(); }}
              placeholder="Search assets…"
              className="pl-10 bg-white/5 border-white/10 text-white"
              data-testid="media-search"
            />
          </div>
          <select
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white/70 text-sm"
            data-testid="media-filter-kind"
          >
            <option value="">All Types</option>
            <option value="image">Images</option>
            <option value="pdf">PDFs</option>
          </select>
          <Button
            variant="outline"
            size="sm"
            className="border-white/20 text-white hover:bg-white/10"
            onClick={fetchLibrary}
          >
            <Filter className="w-4 h-4 mr-1" />
            Apply
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10 gap-2"
            onClick={() => { setUploaderKind("image"); setShowUploader(true); }}
            data-testid="upload-image-btn"
          >
            <Upload className="w-4 h-4" />
            Upload Image
          </Button>
          <Button
            className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
            onClick={() => { setUploaderKind("pdf"); setShowUploader(true); }}
            data-testid="upload-pdf-btn"
          >
            <Upload className="w-4 h-4" />
            Upload PDF
          </Button>
        </div>
      </div>

      {/* Quick upload panel */}
      {showUploader && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="p-4 bg-white/[0.02] border border-white/10 rounded-xl"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-medium">
              Upload {uploaderKind === "image" ? "Image" : "PDF"}
            </h3>
            <Button
              variant="ghost"
              size="sm"
              className="text-white/50 hover:text-white"
              onClick={() => setShowUploader(false)}
            >
              Close
            </Button>
          </div>
          <MediaUploadField
            kind={uploaderKind}
            testIdPrefix={`library-${uploaderKind}`}
            onChange={(media) => {
              if (media && typeof media === "object") {
                fetchLibrary();
                setShowUploader(false);
              }
            }}
          />
        </motion.div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 bg-white/[0.02] border border-white/[0.05] rounded-xl">
          <ImageIcon className="w-10 h-10 text-white/20 mx-auto mb-3" />
          <p className="text-white/50">No assets yet. Upload your first image or PDF.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((item) => {
            const url = `${API_URL}${item.public_url}`;
            return (
              <div
                key={item.id}
                className="group relative bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden hover:border-white/20 transition-all"
                data-testid="media-item"
              >
                <div className="aspect-square bg-slate-900 flex items-center justify-center overflow-hidden">
                  {item.kind === "image" ? (
                    <img
                      src={url}
                      alt={item.alt_text || item.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex flex-col items-center text-blue-300">
                      <FileText className="w-12 h-12 mb-2" />
                      <span className="text-xs uppercase tracking-wider">PDF</span>
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <p className="text-white text-sm font-medium truncate" title={item.title}>
                    {item.title}
                  </p>
                  <p className="text-white/40 text-xs mt-0.5">
                    {formatBytes(item.size)} · {item.kind}
                  </p>
                </div>
                {/* Hover actions */}
                <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-white hover:bg-white/10"
                    onClick={() => window.open(url, "_blank")}
                    title="Open"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-white hover:bg-white/10"
                    onClick={() => handleCopy(item)}
                    title="Copy URL"
                  >
                    {copiedId === item.id ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-400" />
                    ) : (
                      <Copy className="w-3.5 h-3.5" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-white hover:text-red-400"
                    onClick={() => handleDelete(item.id)}
                    title="Archive"
                    data-testid="media-delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
